import React from 'react';
import { Box, ListItem, Typography } from '@mui/material';
import { SportEvent } from '../types/SportEventTypes';
import { EventActions } from './EventActions';
import {
  regularEventContainerStyle,
  timeTextStyle,
  descriptionTextStyle,
  teamLogoStyle,
  emptyPlaceholderStyle,
} from '../styles/playByPlayStyles';
import { shouldHideTeamLogo, getEventTypeIcon } from './PlayByPlayUtils';

interface RegularEventItemProps {
  event: SportEvent;
  eventIndex: number;
  teamLogoPath: string;
  teamColor: string;
  gameTimePrefix?: string;
  gameTime: string;
  description: string;
  isPeriodEvent?: boolean;
  onDebug: (event: SportEvent) => void;
  onSeekToEvent: (event: SportEvent) => void;
  onClipCreate: (event: SportEvent) => void;
  clipGroupCount?: number;
  /**
   * Optional element rendered just before the clip actions, at the right edge
   * of the row. Used to annotate a row without duplicating its chrome — e.g.
   * the inline running score on NBA scoring events (see ScoreEventItem).
   */
  rightAccessory?: React.ReactNode;
  /** Row just arrived live — plays a one-off highlight that fades out. */
  isNew?: boolean;
  /** Event is past the stream's content end — rendered dimmed and inert. */
  isAhead?: boolean;
}

export const RegularEventItem: React.FC<RegularEventItemProps> = ({
  event,
  eventIndex,
  teamLogoPath,
  gameTimePrefix,
  gameTime,
  description,
  onDebug,
  onSeekToEvent,
  onClipCreate,
  rightAccessory,
  isNew,
  isAhead,
}) => {
  const bgColor = eventIndex % 2 === 0 ? '#1F1F25' : '#17171C';

  return (
    <ListItem sx={regularEventContainerStyle(bgColor, isNew, isAhead)} onClick={() => onSeekToEvent(event)}>
      {/* Time */}
      <Typography sx={{ ...timeTextStyle, color: 'rgba(255,255,255,0.7)' }}>
        {gameTimePrefix && (
          <Box
            component="span"
            sx={(theme) => ({ color: theme.palette.cx.text.tertiary, mr: 0.5 })}
          >
            {gameTimePrefix}
          </Box>
        )}
        {gameTime}
      </Typography>

      {/* Team Logo or Empty Placeholder */}

      {(() => {
        const IconComponent = getEventTypeIcon(event?.payload?.type);
        if (IconComponent) {
          return (
            <Box
              sx={{
                ...emptyPlaceholderStyle,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <IconComponent size={20} />
            </Box>
          );
        }
        if (shouldHideTeamLogo(event?.payload?.type)) {
          return <Box sx={emptyPlaceholderStyle} />;
        }
        return teamLogoPath ? (
          <Box component="img" src={teamLogoPath} alt="team" sx={teamLogoStyle} />
        ) : (
          <Box sx={{ width: 32, height: 32 }} />
        );
      })()}

      {/* Description */}
      <Typography sx={{ ...descriptionTextStyle, color: 'rgba(255,255,255,0.9)' }}>
        {description}
      </Typography>
      {/* Optional right-edge annotation (e.g. inline running score) */}
      {rightAccessory}

      {/* Actions */}
      {event.payload?.type === 'period_start' ? null : (
        <EventActions event={event} onDebug={onDebug} onClipCreate={onClipCreate} disabled={isAhead} />
      )}
    </ListItem>
  );
};
